import { FutureFlowEntity, Instrument, Portfolio } from "../../domain/entities/DashboardEntity";
import { enrichFutureFlow } from "../../infrastructure/DashboardMapper";
import {
    useGetFutureFlows,
    useGetInstruments,
    useGetInstrumentTypes,
    useGetPortfolios,
} from "./useDashboardQueries";

import { useMemo } from "react";

type OptionItem = { id: number | string; name: string; detail?: string };

const mapToOptions = (data: OptionItem[] = []) => {
    return data.map(({ id, name, detail }) => ({
        detail,
        label: name,
        value: id.toString(),
    }));
};

export const useFutureFlowsData = () => {
    const { data: futureFlowData, isLoading: futureFlowIsLoading } = useGetFutureFlows();
    const { data: portfoliosData, isLoading: portfoliosIsLoading } = useGetPortfolios();
    const { data: instrumentsData, isLoading: instrumentsIsLoading } = useGetInstruments();
    const { data: instrumentTypesData, isLoading: instrumentTypesIsLoading } = useGetInstrumentTypes();

    const portfolioOptions = useMemo(
        () => mapToOptions(portfoliosData?.data),
        [portfoliosData]
    );

    // 1) Construir futureFlow enriched solo cuando cambian las dependencias
    const futureFlow: FutureFlowEntity[] = useMemo(() => {
        const result: FutureFlowEntity[] = [];
        if (futureFlowData?.data) {
            for (const item of futureFlowData.data) {
                const { id, portfolio, instrument, value, flowDate } = item;
                const enrichedItem = enrichFutureFlow(
                    { flowDate, portfolio, instrument, value },
                    id,
                    {
                        portfolios: portfoliosData?.data as unknown as Portfolio[],
                        instruments: instrumentsData?.data as unknown as Instrument[],
                    }
                );
                if (enrichedItem) {
                    result.push(enrichedItem);
                }
            }
        }
        return result;
    }, [futureFlowData, portfoliosData, instrumentsData]);

    // 2) Extraer años únicos, ordenarlos y mapear a OptionItem[]
    const years = useMemo(() => {
        const yearSet = new Set<number>();
        for (const ff of futureFlow) {
            const y = ff.flowDate.getFullYear();
            yearSet.add(y);
        }

        const yearOptions = Array.from(yearSet)
            .sort((a, b) => a - b)
            .map((y) => ({
                id: y,
                name: y.toString(),
            }));

        return [
            { id: "all", name: "Todos" },
            ...yearOptions,
        ].map(({ id, name }) => ({
            label: name,
            value: id.toString(),
        }));
    }, [futureFlow]);

    const isLoading =
        portfoliosIsLoading ||
        instrumentsIsLoading ||
        instrumentTypesIsLoading ||
        futureFlowIsLoading;

    return {
        years,
        futureFlow,
        instrumentsTypes: instrumentTypesData?.data,
        portfolioOptions,
        isLoading,
    };
};
